import {
    LayoutDashboard, Users, Home, BarChart3, Globe, Boxes, FolderKanban,
    QrCode, LifeBuoy, GitCompare, CreditCard, Building2,
    ClipboardList, Bell, BookOpen, Database, UserCog, ShieldCheck,
    type LucideIcon,
} from 'lucide-react';
import {
    DASHBOARD_ROUTES, PROPERTY_ROUTES, PROJECT_ROUTES, LEAD_ROUTES, QR_ROUTES,
    BUILDER_ROUTES, COMPARE_ROUTES, BILLING_ROUTES, ADMIN_ROUTES,
    SALES_TEAM_ROUTES, SUPPORT_ROUTES,
} from '@/api/constants/app.url';
import type { DashboardRoleId } from '@/shared/utils/dashboard-role';
import type { PermissionKey } from '@/shared/permissions';
import type { ActivationKey } from '@/shared/access/module-activation';

export interface SidebarNavItem {
    to: string;
    label: string;
    icon: LucideIcon;
    end?: boolean;
    tag?: string;
    /**
     * Role Matrix permission this module requires (its `view` action). A
     * managed member whose role lacks it doesn't see the item at all; an owner
     * blocked by their PLAN sees it locked with the Upgrade popup on click.
     * Owners with plan access always pass.
     */
    feature?: PermissionKey;
    /**
     * Show ONLY to members whose role explicitly grants `feature` — owners do
     * NOT get it for free.
     *
     * Owners (tenant_admin) normally bypass the Role Matrix and see every item
     * in their variant. That is wrong for a module that doesn't belong to the
     * workspace's own business: a Builder or Realtor workspace has no "builder
     * partners" of its own, so its OWNER must never see Builders — while a
     * builder/sales login inside a Developer workspace still gets it the moment
     * the developer ticks the permission.
     */
    requiresGrant?: boolean;
    /**
     * Module the tenant has to switch ON themselves before it exists for them
     * (Website Analytics needs its tracker activated in Settings →
     * Integrations). Until then the item is HIDDEN — not locked: no plan or
     * permission change unlocks it, only flipping the switch does.
     */
    activation?: ActivationKey;
    /**
     * Makes the item a collapsible GROUP: the row expands to these links rather
     * than navigating itself (its `to` is only used when the rail is collapsed
     * to icons, where it stands in for the whole group). Children are gated
     * individually by their own `feature`.
     */
    children?: SidebarNavItem[];
}

/**
 * Inventory — Projects (groupings of properties) and the Properties list. Both
 * ride on the Properties permission, so a role that can see properties sees
 * the whole group.
 */
const INVENTORY_GROUP: SidebarNavItem = {
    to: PROJECT_ROUTES.list, label: 'Inventory', icon: Boxes, feature: 'properties.view',
    children: [
        { to: PROJECT_ROUTES.list,  label: 'Projects',   icon: FolderKanban, feature: 'properties.view' },
        { to: PROPERTY_ROUTES.list, label: 'Properties', icon: Home,         feature: 'properties.view' },
    ],
};

// The Role Matrix deliberately has NO sidebar item — it's reached through
// Settings only (the hub card shows on developer/builder variants, matching
// seed_default_roles, which creates roles only for those workspaces).
//
// Every permission-gated module is listed in EVERY non-admin variant so the
// Role Matrix stays the single source of truth: an item renders only when the
// member's role grants its `feature`, so listing it here can't widen access —
// omitting it, however, would make a granted permission un-clickable.
const SIDEBAR_NAV: Record<DashboardRoleId, SidebarNavItem[]> = {
    agent: [
        { to: DASHBOARD_ROUTES.HOME, label: 'Home', icon: LayoutDashboard, end: true, feature: 'dashboard.view' },
        { to: LEAD_ROUTES.list, label: 'Leads', icon: ClipboardList, feature: 'leads.view' },
        INVENTORY_GROUP,
        { to: BUILDER_ROUTES.list, label: 'Builders', icon: Building2, feature: 'builders.view', requiresGrant: true },
        { to: QR_ROUTES.list, label: 'QR Codes', icon: QrCode, feature: 'qr_codes.view' },
        { to: COMPARE_ROUTES.root, label: 'Compare', icon: GitCompare, feature: 'compare.view' },
        { to: DASHBOARD_ROUTES.ANALYTICS, label: 'Analytics', icon: BarChart3, feature: 'analytics.view' },
        { to: DASHBOARD_ROUTES.WEB_ANALYTICS, label: 'Website Analytics', icon: Globe, feature: 'web_analytics.view', activation: 'web_analytics' },
        { to: BILLING_ROUTES.root, label: 'Billing', icon: CreditCard, feature: 'billing.view' },
        { to: SUPPORT_ROUTES.list, label: 'Support', icon: LifeBuoy, feature: 'support.view' },
    ],
    team: [
        { to: DASHBOARD_ROUTES.HOME, label: 'Home', icon: LayoutDashboard, end: true, feature: 'dashboard.view' },
        { to: LEAD_ROUTES.list, label: 'Leads', icon: ClipboardList, feature: 'leads.view' },
        INVENTORY_GROUP,
        { to: BUILDER_ROUTES.list, label: 'Builders', icon: Building2, feature: 'builders.view', requiresGrant: true },
        { to: SALES_TEAM_ROUTES.list, label: 'Sales Team', icon: Users, feature: 'sales_team.view' },
        { to: QR_ROUTES.list, label: 'QR Codes', icon: QrCode, feature: 'qr_codes.view' },
        { to: COMPARE_ROUTES.root, label: 'Compare', icon: GitCompare, feature: 'compare.view' },
        { to: DASHBOARD_ROUTES.ANALYTICS, label: 'Analytics', icon: BarChart3, feature: 'analytics.view' },
        { to: DASHBOARD_ROUTES.WEB_ANALYTICS, label: 'Website Analytics', icon: Globe, feature: 'web_analytics.view', activation: 'web_analytics' },
        { to: BILLING_ROUTES.root, label: 'Billing', icon: CreditCard, feature: 'billing.view' },
        { to: SUPPORT_ROUTES.list, label: 'Support', icon: LifeBuoy, feature: 'support.view' },
    ],
    // Builders is listed here but gated like every other module: a builder
    // account does NOT get it by default (the Builder role grants no
    // builders.* permission), and it appears only once a developer ticks
    // Builders on the Builder role — or on that individual builder's
    // permissions — in the Role Matrix.
    builder: [
        { to: DASHBOARD_ROUTES.HOME, label: 'Home', icon: LayoutDashboard, end: true, feature: 'dashboard.view' },
        INVENTORY_GROUP,
        { to: BUILDER_ROUTES.list, label: 'Builders', icon: Building2, feature: 'builders.view', requiresGrant: true },
        { to: SALES_TEAM_ROUTES.list, label: 'Sales Team', icon: Users, feature: 'sales_team.view' },
        { to: LEAD_ROUTES.list, label: 'Leads', icon: ClipboardList, feature: 'leads.view' },
        { to: QR_ROUTES.list, label: 'QR Codes', icon: QrCode, feature: 'qr_codes.view' },
        { to: COMPARE_ROUTES.root, label: 'Compare', icon: GitCompare, feature: 'compare.view' },
        { to: DASHBOARD_ROUTES.ANALYTICS, label: 'Analytics', icon: BarChart3, feature: 'analytics.view' },
        { to: DASHBOARD_ROUTES.WEB_ANALYTICS, label: 'Website Analytics', icon: Globe, feature: 'web_analytics.view', activation: 'web_analytics' },
        { to: BILLING_ROUTES.root, label: 'Billing', icon: CreditCard, feature: 'billing.view' },
        { to: SUPPORT_ROUTES.list, label: 'Support', icon: LifeBuoy, feature: 'support.view' },
    ],
    developer: [
        { to: DASHBOARD_ROUTES.HOME, label: 'Home', icon: LayoutDashboard, end: true, feature: 'dashboard.view' },
        INVENTORY_GROUP,
        { to: BUILDER_ROUTES.list, label: 'Builders', icon: Building2, feature: 'builders.view' },
        { to: SALES_TEAM_ROUTES.list, label: 'Sales Team', icon: Users, feature: 'sales_team.view' },
        { to: LEAD_ROUTES.list, label: 'Leads', icon: ClipboardList, feature: 'leads.view' },
        { to: QR_ROUTES.list, label: 'QR Codes', icon: QrCode, feature: 'qr_codes.view' },
        { to: COMPARE_ROUTES.root, label: 'Compare', icon: GitCompare, feature: 'compare.view' },
        { to: DASHBOARD_ROUTES.ANALYTICS, label: 'Analytics', icon: BarChart3, feature: 'analytics.view' },
        { to: DASHBOARD_ROUTES.WEB_ANALYTICS, label: 'Website Analytics', icon: Globe, feature: 'web_analytics.view', activation: 'web_analytics' },
        { to: BILLING_ROUTES.root, label: 'Billing', icon: CreditCard, feature: 'billing.view' },
        { to: SUPPORT_ROUTES.list, label: 'Support', icon: LifeBuoy, feature: 'support.view' },
    ],
    // The super-admin variant is not permission-gated: the Role Matrix scopes
    // members inside a tenant, and a platform super_admin has no tenant role.
    // Access is enforced by ProtectedRoute roles={['super_admin']} instead.
    admin: [
        { to: ADMIN_ROUTES.INSIGHT,         label: 'Home',            icon: LayoutDashboard, end: true },
        { to: ADMIN_ROUTES.CLIENT_ACCOUNTS, label: 'Client Accounts', icon: Building2 },
        { to: ADMIN_ROUTES.DATABASE,        label: 'Database',        icon: Database },
        { to: ADMIN_ROUTES.ANALYTICS,       label: 'Analytics',       icon: BarChart3 },
        { to: ADMIN_ROUTES.PLANS,           label: 'Plans & Pricing', icon: CreditCard },
        { to: ADMIN_ROUTES.TICKETS,         label: 'Tickets',         icon: LifeBuoy },
        { to: ADMIN_ROUTES.NOTIFICATIONS,   label: 'Notifications',   icon: Bell },
        { to: ADMIN_ROUTES.HELP_CONTROLS,   label: 'Help controls',   icon: BookOpen },
        { to: ADMIN_ROUTES.PROFILE,         label: 'Profile',         icon: UserCog },
        { to: ADMIN_ROUTES.SECURITY,        label: 'Security',        icon: ShieldCheck },
    ],
};

export function getSidebarNav(roleId: DashboardRoleId): SidebarNavItem[] {
    return SIDEBAR_NAV[roleId];
}
